import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

export interface ToastProps {
  message: string
  action?: {
    label: string
    onClick: () => void
  }
  onDismiss: () => void
  duration?: number
}

interface ToastData {
  id: string
  message: string
  action?: {
    label: string
    onClick: () => void
  }
  duration: number
}

interface ToastContextValue {
  showToast: (
    message: string,
    options?: {
      action?: { label: string; onClick: () => void }
      duration?: number
    }
  ) => void
  dismissToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

export function Toast({ message, action, onDismiss, duration = 5000 }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, duration)
    return () => clearTimeout(timer)
  }, [onDismiss, duration])

  const containerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: 'var(--color-ink)',
    color: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-elevated)',
    fontFamily: 'var(--font-body)',
    fontSize: '0.875rem',
    lineHeight: 1.4,
  }

  const messageStyles: React.CSSProperties = {
    flex: 1,
  }

  const buttonStyles: React.CSSProperties = {
    padding: '4px 12px',
    backgroundColor: 'transparent',
    color: 'var(--color-accent)',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  }

  const dismissStyles: React.CSSProperties = {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    color: 'var(--color-ink-subtle)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  return (
    <div style={containerStyles} role="alert" aria-live="polite">
      <span style={messageStyles}>{message}</span>
      {action && (
        <button
          type="button"
          style={buttonStyles}
          onClick={() => {
            action.onClick()
            onDismiss()
          }}
        >
          {action.label}
        </button>
      )}
      <button
        type="button"
        style={dismissStyles}
        onClick={onDismiss}
        aria-label="Dismiss notification"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M4 4L12 12M12 4L4 12"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  )
}

interface ToastProviderProps {
  children: ReactNode
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastData[]>([])

  const showToast = useCallback(
    (
      message: string,
      options?: {
        action?: { label: string; onClick: () => void }
        duration?: number
      }
    ) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      const newToast: ToastData = {
        id,
        message,
        action: options?.action,
        duration: options?.duration ?? 5000,
      }
      setToasts((prev) => [...prev, newToast])
    },
    []
  )

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const containerStyles: React.CSSProperties = {
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    zIndex: 9999,
    pointerEvents: 'none',
  }

  const toastWrapperStyles: React.CSSProperties = {
    pointerEvents: 'auto',
  }

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <div style={containerStyles}>
        <AnimatePresence mode="sync">
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              style={toastWrapperStyles}
            >
              <Toast
                message={toast.message}
                action={toast.action}
                onDismiss={() => dismissToast(toast.id)}
                duration={toast.duration}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  )
}
